import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Fingerprint, ChevronDown, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Clock, Ban, Plus, FileDown, ClipboardCheck, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeName } from "@/components/EmployeeName";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useQuickProfile } from "@/context/QuickProfileContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPE_STYLE = {
  FULL_DAY: "bg-[#E9F9EF] text-[#0E8A46]",
  HALF_DAY_AM: "bg-[#E7F0FF] text-[#0058C7]",
  HALF_DAY_PM: "bg-[#E7F0FF] text-[#0058C7]",
  WORKING: "bg-[#E6F7F5] text-[#0E7C86]",
  NEEDS_REVIEW: "bg-[#FFF1E6] text-[#B5590F]",
  IGNORED: "bg-[#F1F1F4] text-[#6E6E73]",
};
const STATUS_STYLE = {
  AUTO_APPROVED: "bg-[#E9F9EF] text-[#0E8A46]",
  MANUALLY_APPROVED: "bg-[#EAF0FF] text-[#0058C7]",
  IN_PROGRESS: "bg-[#E6F7F5] text-[#0E7C86]",
  NEEDS_REVIEW: "bg-[#FFF1E6] text-[#B5590F]",
  UNMAPPED_PIN: "bg-[#FFF1E6] text-[#B5590F]",
  IGNORED: "bg-[#F1F1F4] text-[#6E6E73]",
};
const STATUS_LABEL = { IN_PROGRESS: "Working", UNMAPPED_PIN: "Unmapped PIN" };
const statusLabel = (s) => STATUS_LABEL[s] || (s || "").replace(/_/g, " ");
const TYPE_LABEL = { WORKING: "Working" };
const typeLabel = (t) => TYPE_LABEL[t] || (t || "").replace(/_/g, " ");
const SCAN_COLOR = {
  VALID_IN: "text-[#0E8A46]",
  VALID_OUT: "text-[#0058C7]",
};
const SCAN_STYLE = (k) => SCAN_COLOR[k] || "text-[#A1A1A6]";

const todayMakassar = () => {
  const now = new Date(Date.now() + 8 * 3600 * 1000);
  return now.toISOString().slice(0, 10);
};

const EN_MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const fmtGoLive = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `${EN_MONTHS[m - 1]} ${d}, ${y}`;
};
const REASON_LABEL = {
  MISSING_PAIR: "Missing Clock-In/Clock-Out Pair",
  LATE_ARRIVAL: "Late Arrival",
  EARLY_DEPARTURE: "Early Departure",
  AMBIGUOUS_HALF_DAY: "Ambiguous Half Day",
  UNUSUAL_SCAN_PATTERN: "Unusual Scan Pattern",
  NEW_SCAN_AFTER_REVIEW: "New Scan After Review",
  MANUAL_ENTRY: "Manual Entry",
};
const reasonLabel = (c) => REASON_LABEL[c] || (c || "").replace(/_/g, " ");

const Card = ({ icon: Icon, label, value, color, onClick }) => (
  <div onClick={onClick} className={`liquid-glass-card flex items-center gap-3 p-4 ${onClick ? "cursor-pointer transition-all hover:shadow-md active:scale-[0.98]" : ""}`}>
    <span className="grid h-10 w-10 place-items-center rounded-full" style={{ background: `${color}1A`, color }}>
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <p className="num text-[22px] font-bold leading-none">{value}</p>
      <p className="mt-1 text-[12px] text-[#8E8E93]">{label}</p>
    </div>
  </div>
);

export default function FingerspotAttendance() {
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(() => searchParams.get("date") || todayMakassar());
  const [statusF, setStatusF] = useState(() => searchParams.get("status") || (searchParams.get("employee") ? "NEEDS_REVIEW" : "all"));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [peek, setPeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});
  const [employees, setEmployees] = useState([]);
  const [manOpen, setManOpen] = useState(false);
  const [man, setMan] = useState({ employee_id: "", date: todayMakassar(), action: "approve", day_value: 1, note: "" });
  const navigate = useNavigate();
  const { openQuickProfile } = useQuickProfile();
  const { t } = useT();
  const { readOnly } = useAuth();
  const stLabel = (s) => ({ AUTO_APPROVED: t("fp.st.auto"), MANUALLY_APPROVED: t("fp.st.manual"), IN_PROGRESS: t("fp.st.working"), NEEDS_REVIEW: t("fp.st.needs"), UNMAPPED_PIN: t("fp.st.unmapped"), IGNORED: t("fp.st.ignored") }[s] || (s || "").replace(/_/g, " "));
  const tyLabel = (ty) => ({ FULL_DAY: t("fp.ty.full"), HALF_DAY_AM: t("fp.ty.half"), HALF_DAY_PM: t("fp.ty.half"), WORKING: t("fp.ty.working"), NEEDS_REVIEW: t("fp.ty.needs"), IGNORED: t("fp.ty.ignored") }[ty] || (ty || "").replace(/_/g, " "));
  const rsLabel = (c) => ({ MISSING_PAIR: t("fp.rs.missing"), LATE_ARRIVAL: t("fp.rs.late"), EARLY_DEPARTURE: t("fp.rs.early"), AMBIGUOUS_HALF_DAY: t("fp.rs.ambiguous"), UNUSUAL_SCAN_PATTERN: t("fp.rs.unusual"), NEW_SCAN_AFTER_REVIEW: t("fp.rs.newscan"), MANUAL_ENTRY: t("fp.rs.manual") }[c] || (c || "").replace(/_/g, " "));
  const scanLabel = (k) => ({ VALID_IN: t("fp.sk.in"), VALID_OUT: t("fp.sk.out"), DUPLICATE_IGNORED: t("fp.sk.dup"), REPEATED_ARRIVAL_IGNORED: t("fp.sk.repeat"), EXTRA_IGNORED: t("fp.sk.extra"), MANUAL_IGNORED: t("fp.sk.manual") }[k] || (k || "").replace(/_/g, " "));
  const nowD = new Date();
  const [exportOpen, setExportOpen] = useState(false);
  const [exp, setExp] = useState({ bulan: nowD.getMonth() + 1, tahun: nowD.getFullYear(), scope: "all", employee_id: "" });
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      if (statusF !== "all") params.status = statusF;
      const [d, s] = await Promise.all([
        api.get("/attendance/fingerprint/daily", { params }),
        api.get("/attendance/fingerprint/summary", { params: { date } }),
      ]);
      setRows(d.data);
      setSummary(s.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [date, statusF]);

  const openPeek = async (title, keys) => {
    setPeek({ title, rows: null });
    try {
      const { data } = await api.get("/attendance/day-summary", { params: { date } });
      const g = data.groups || {};
      const rows = keys.flatMap((k) => g[k] || []).sort((a, b) => (a.nama || "").localeCompare(b.nama || ""));
      setPeek({ title, rows });
    } catch { setPeek({ title, rows: [] }); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const submitManual = async () => {
    if (!man.employee_id) { toast.error(t("fp.selEmp")); return; }
    if (!man.note.trim()) { toast.error(t("fp.noteReq")); return; }
    setBusy("manual");
    try {
      await api.post("/attendance/fingerprint/manual", {
        employee_id: man.employee_id, date: man.date, action: man.action,
        day_value: man.action === "ignore" ? 0 : Number(man.day_value), note: man.note,
      });
      toast.success(t("fp.manSaved"));
      setManOpen(false);
      setMan({ employee_id: "", date: man.date, action: "approve", day_value: 1, note: "" });
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const recompute = async () => {
    setBusy("recompute");
    try {
      const { data } = await api.post("/attendance/fingerprint/recompute", null, { params: { date } });
      toast.success(t("fp.processed", { n: data.processed }));
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const review = async (id, action, manual_day_value) => {
    setBusy(id);
    try {
      await api.post(`/attendance/fingerprint/${id}/review`, {
        action, manual_day_value, manual_note: notes[id] || "",
      });
      toast.success(t("fp.saved"));
      setEditing((e) => ({ ...e, [id]: false }));
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const revert = async (id) => {
    setBusy(id);
    try {
      await api.post(`/attendance/fingerprint/${id}/revert`, {});
      toast.success(t("fp.reverted"));
      setEditing((e) => ({ ...e, [id]: false }));
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const doExport = async () => {
    if (exp.scope === "one" && !exp.employee_id) { toast.error(t("fp.selEmp")); return; }
    setExporting(true);
    try {
      const params = { bulan: exp.bulan, tahun: exp.tahun };
      if (exp.scope === "one") params.employee_id = exp.employee_id;
      const res = await api.get("/attendance/pdf/monthly", { params, responseType: "blob" });
      const disp = res.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = m ? m[1] : "Laporan_Kehadiran.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("fp.pdfDownloaded"));
      setExportOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="fingerprint-attendance-page" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-[22px] font-semibold tracking-tight sm:text-[28px]">
          <Fingerprint className="h-6 w-6 text-[#0071E3]" /> {t("fp.title")}
        </h1>
        <p className="mt-0.5 text-[13px] text-[#8E8E93]">
          {t("fp.subtitle")}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            data-testid="fp-open-eod"
            onClick={() => navigate("/attendance/end-of-day")}
            className="flex items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3.5 py-1.5 text-[12.5px] font-medium text-[#1D1D1F] transition-all hover:bg-white active:scale-[0.98]"
          >
            <ClipboardCheck className="h-3.5 w-3.5 text-[#0071E3]" /> {t("fp.eod")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Card icon={CheckCircle2} label={t("ma.fullDay")} value={summary?.full_day ?? 0} color="#0E8A46" onClick={() => openPeek(t("ma.fullDay"), ["full"])} />
        <Card icon={Clock} label={t("ma.halfDay")} value={summary?.half_day ?? 0} color="#0058C7" onClick={() => openPeek(t("ma.halfDay"), ["half"])} />
        <Card icon={Fingerprint} label={t("fp.working")} value={summary?.currently_working ?? 0} color="#0E7C86" onClick={() => openPeek(t("fp.working"), ["working"])} />
        <Card icon={AlertTriangle} label={t("ma.needsReview")} value={summary?.needs_review ?? 0} color="#B5590F" onClick={() => openPeek(t("ma.needsReview"), ["needs"])} />
        <Card icon={Ban} label={t("ma.notArrived")} value={summary?.not_complete ?? 0} color="#8E8E93" onClick={() => openPeek(t("ma.notArrived"), ["absent"])} />
      </div>

      <Dialog open={!!peek} onOpenChange={(v) => !v && setPeek(null)}>
        <DialogContent data-testid="fp-peek-dialog" className="max-h-[80vh] gap-0 overflow-hidden rounded-[20px] border border-white/50 bg-white/80 p-0 backdrop-blur-2xl sm:max-w-[460px]">
          <DialogHeader className="border-b border-black/[0.06] px-5 py-4 text-left">
            <DialogTitle className="text-[16px] font-semibold tracking-tight">{peek?.title} · {peek?.rows?.length ?? 0}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto p-3">
            {!peek?.rows ? (
              <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
            ) : peek.rows.length === 0 ? (
              <p className="py-10 text-center text-[13.5px] text-[#8E8E93]">{t("att.noneInGroup")}</p>
            ) : (
              <div className="space-y-1.5">
                {peek.rows.map((p) => (
                  <div key={p.employee_id || p.nip} data-testid="fp-peek-row" className="flex items-center gap-3 rounded-xl px-2.5 py-2 hover:bg-black/[0.03]">
                    <EmployeeAvatar id={p.employee_id} nama={p.nama} hasPhoto={p.has_photo} size={34} />
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 text-[13.5px] font-medium text-[#1D1D1F]"><EmployeeName id={p.employee_id} name={p.nama} />{p.verified && !p.is_jarit && <VerifiedBadge size={14} />}<JaritBadge show={p.is_jarit} /></p>
                      <p className="num text-[11.5px] text-[#8E8E93]">{p.nip || "—"}</p>
                    </div>
                    <span className="num shrink-0 text-[12px] text-[#6E6E73]">{p.clock_in || "--:--"} / {p.clock_out || "--:--"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <input
            data-testid="fp-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 rounded-full border border-[#E1E1E6] bg-white/70 px-3 text-[13px]"
          />
          {summary?.before_golive && (
            <span
              data-testid="fp-before-golive-badge"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#FFF1E6] px-2.5 py-1 text-[11.5px] font-medium text-[#B5590F]"
            >
              <Clock className="h-3.5 w-3.5" /> {t("fp.beforeGolive")}
            </span>
          )}
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="fp-status-filter" className="h-9 w-full rounded-full border-[#E1E1E6] bg-white/70 text-[13px] sm:w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="all">{t("fp.allStatuses")}</SelectItem>
            <SelectItem value="OPEN_ISSUES">{t("fp.openIssues")}</SelectItem>
            <SelectItem value="AUTO_APPROVED">{t("fp.autoApproved")}</SelectItem>
            <SelectItem value="IN_PROGRESS">{t("fp.working")}</SelectItem>
            <SelectItem value="NEEDS_REVIEW">{t("ma.needsReview")}</SelectItem>
            <SelectItem value="MANUALLY_APPROVED">{t("fp.manApproved")}</SelectItem>
            <SelectItem value="IGNORED">{t("fp.ignored")}</SelectItem>
          </SelectContent>
        </Select>
        <button
          data-testid="fp-recompute-btn"
          onClick={recompute}
          disabled={busy === "recompute"}
          className="flex h-9 items-center justify-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[13px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50"
        >
          {busy === "recompute" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("fp.recompute")}
        </button>
        <button
          data-testid="fp-manual-attendance-btn"
          onClick={() => setManOpen(true)}
          className="flex h-9 items-center justify-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-4 text-[13px] font-medium text-[#1D1D1F] transition-all hover:bg-white active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" /> {t("fp.manual")}
        </button>
        <button
          data-testid="fp-export-pdf-btn"
          onClick={() => setExportOpen(true)}
          className="flex h-9 items-center justify-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-4 text-[13px] font-medium text-[#1D1D1F] transition-all hover:bg-white active:scale-[0.98]"
        >
          <FileDown className="h-4 w-4" /> {t("fp.exportPdf")}
        </button>
      </div>

      <div data-testid="fp-table" className="liquid-glass-card overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="grid place-items-center py-14"><Loader2 className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
        ) : rows.length === 0 ? (
          summary?.before_golive ? (
            <div data-testid="fp-before-golive-note" className="grid place-items-center gap-2 px-6 py-14 text-center">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#FFF1E6] text-[#B5590F]">
                <Clock className="h-5 w-5" />
              </span>
              <p className="text-[14px] font-medium text-[#1D1D1F]">{t("fp.beforeGolive")}</p>
              <p className="max-w-md text-[13px] text-[#8E8E93]">
                {t("fp.goliveBody", { date: fmtGoLive(summary?.start_date) })}
              </p>
            </div>
          ) : (
            <p className="py-14 text-center text-[13.5px] text-[#8E8E93]">
              {t("fp.emptyDate")}
            </p>
          )
        ) : (
          <>
            {/* Mobile: stacked cards */}
            <div className="space-y-3 md:hidden">
              {rows.map((r) => {
                const open = expanded[r.id];
                const needs = r.status === "NEEDS_REVIEW";
                if (r.unmapped) {
                  return (
                    <div key={r.id} data-testid="fp-unmapped-card" className="rounded-2xl border border-[#FFE2CC] bg-[#FFF8F2] p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[14px] font-semibold text-[#B5590F]">{t("fp.unknownEmp")}</p>
                          <p className="num text-[11.5px] text-[#8E8E93]">PIN {r.employee_pin} · {r.attendance_date}</p>
                        </div>
                        <span className="shrink-0 rounded-full bg-[#FFF1E6] px-2.5 py-1 text-[11px] font-medium text-[#B5590F]">{t("fp.unmappedPin")}</span>
                      </div>
                      <p className="num mt-2 text-[12px] text-[#8E8E93]">{t("fp.scans")}: {r.first_scan_at?.slice(0, 5)} → {r.last_scan_at?.slice(0, 5)} ({r.scan_count})</p>
                      <button data-testid="fp-map-pin" onClick={() => navigate("/integrations/fingerspot")} className="mt-3 w-full rounded-full bg-[#0071E3] py-2 text-[12.5px] font-semibold text-white transition-all active:scale-[0.98]">{t("fp.mapPin")}</button>
                    </div>
                  );
                }
                const events = (r.scan_events && r.scan_events.length)
                  ? r.scan_events
                  : (r.raw_scans || []).map((s) => ({ t: s.slice(11, 16), kind: null }));
                return (
                  <div key={r.id} data-testid="fp-card" className="rounded-2xl border border-black/[0.05] bg-white/70 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <button onClick={() => openQuickProfile(r.employee_id)} className="flex min-w-0 items-center gap-2.5 text-left">
                        <EmployeeAvatar id={r.employee_id} nama={r.employee_name} hasPhoto={r.has_photo} size={34} />
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 text-[14px] font-semibold text-[#1D1D1F] hover:text-[#0071E3]"><span className="truncate">{r.employee_name}</span>{r.verified && !r.is_jarit && <VerifiedBadge size={14} />}<JaritBadge show={r.is_jarit} /></p>
                          <p className="num text-[11.5px] font-medium text-[#0071E3]">{r.employee_nip || "—"}</p>
                        </div>
                      </button>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${STATUS_STYLE[r.status] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
                        {stLabel(r.status)}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                      <div className="rounded-xl bg-black/[0.03] py-2">
                        <p className="text-[10px] uppercase tracking-wide text-[#8E8E93]">{t("fp.firstLast")}</p>
                        <p className="num mt-0.5 text-[13px] font-semibold">{r.first_scan_at?.slice(0, 5)} → {r.last_scan_at?.slice(0, 5)}</p>
                      </div>
                      <div className="rounded-xl bg-black/[0.03] py-2">
                        <p className="text-[10px] uppercase tracking-wide text-[#8E8E93]">{t("fp.result")}</p>
                        <p className="mt-0.5 text-[12.5px] font-semibold">
                          {r.manual_day_value != null ? `${r.manual_day_value}${t("fp.dayUnit")} ${t("fp.manualSuffix")}` : r.calculated_day_value != null ? `${r.calculated_day_value}${t("fp.dayUnit")}` : tyLabel(r.attendance_type)}
                        </p>
                      </div>
                    </div>
                    {needs && r.reason_code && <p className="mt-2 text-[12px] font-medium text-[#B5590F]">{rsLabel(r.reason_code)}</p>}
                    <button
                      onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                      className="mt-2.5 flex items-center gap-1 text-[12px] font-medium text-[#0071E3]"
                    >
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {t("fp.todayScans", { n: r.scan_count })}
                    </button>
                    {open && (
                      <div className="mt-1.5 space-y-1">
                        {events.map((s, i) => (
                          <div key={i} className="flex items-center justify-between rounded-lg bg-black/[0.03] px-2.5 py-1.5">
                            <span className="num text-[12.5px] font-medium">{s.t}</span>
                            <span className={`text-[12px] ${SCAN_STYLE(s.kind)}`}>{scanLabel(s.kind)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(r.manual_note || r.reviewed_by) && (
                      <p className="mt-2 flex flex-wrap items-center gap-1 text-[11.5px] text-[#8E8E93]">
                        {r.manual_note || "—"}
                        {r.reviewed_by && <span className="inline-flex items-center gap-1">· {t("ma.by", { name: r.reviewed_by })} <VerifiedBadge size={12} /></span>}
                      </p>
                    )}
                    <div className="mt-3 space-y-2">
                      {editing[r.id] ? (
                        <>
                          <p className="text-[11px] font-medium text-[#8E8E93]">{t("fp.editResult")}{r.status === "AUTO_APPROVED" ? t("fp.reasonReqSuffix") : ""}</p>
                          <div className="grid grid-cols-4 gap-1.5">
                            <RvBtn onClick={() => review(r.id, "approve", 1)} busy={busy === r.id}>1.0</RvBtn>
                            <RvBtn onClick={() => review(r.id, "approve", 0.5)} busy={busy === r.id}>0.5</RvBtn>
                            <RvBtn onClick={() => review(r.id, "approve", 0)} busy={busy === r.id}>0</RvBtn>
                            <RvBtn onClick={() => review(r.id, "ignore")} busy={busy === r.id} tone="gray">{t("fp.ignore")}</RvBtn>
                          </div>
                          <input
                            value={notes[r.id] || ""}
                            onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                            placeholder={r.status === "AUTO_APPROVED" ? t("fp.reasonReqEdit") : t("fp.noteOpt")}
                            className="h-9 w-full rounded-lg border border-[#E1E1E6] bg-white/70 px-3 text-[12.5px]"
                          />
                          <div className="flex items-center gap-3">
                            <button data-testid="fp-edit-cancel" onClick={() => setEditing((e) => ({ ...e, [r.id]: false }))} className="flex items-center gap-1 text-[12px] font-medium text-[#6E6E73]"><X className="h-3.5 w-3.5" /> {t("fp.cancel")}</button>
                            {r.manual_override && (
                              <button data-testid="fp-revert" onClick={() => revert(r.id)} disabled={busy === r.id} className="text-[12px] font-medium text-[#0071E3]">{t("fp.revertAutoResult")}</button>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button data-testid="fp-edit-btn" onClick={() => setEditing((e) => ({ ...e, [r.id]: true }))} className={`flex items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3 py-1.5 text-[12.5px] font-medium text-[#0071E3] transition-all active:scale-[0.97] ${readOnly ? "hidden" : ""}`}><Pencil className="h-3.5 w-3.5" /> {t("fp.edit")}</button>
                          {r.manual_override && (
                            <button data-testid="fp-revert" onClick={() => revert(r.id)} disabled={busy === r.id} className="text-[12px] font-medium text-[#0071E3]">{t("fp.revertAuto")}</button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: table */}
            <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-y border-black/[0.06] text-[10.5px] uppercase tracking-wider text-[#A1A1A6]">
                  <th className="py-2.5 pl-5 pr-3 font-semibold">{t("fp.employee")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("fp.firstLast")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("fp.scans")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("fp.result")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("fp.status")}</th>
                  <th className="px-3 py-2.5 pr-5 text-right font-semibold">{t("fp.review")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const open = expanded[r.id];
                  const needs = r.status === "NEEDS_REVIEW";
                  if (r.unmapped) {
                    return (
                      <tr key={r.id} data-testid="fp-unmapped-row" className="border-b border-black/[0.04] hover:bg-white/50">
                        <td className="py-3 pl-5 pr-3">
                          <p className="text-[13px] font-medium text-[#B5590F]">{t("fp.unknownEmp")}</p>
                          <p className="num text-[11.5px] text-[#8E8E93]">PIN {r.employee_pin} · {r.attendance_date}</p>
                        </td>
                        <td className="num whitespace-nowrap px-3 text-[13px]">{r.first_scan_at?.slice(0, 5)} → {r.last_scan_at?.slice(0, 5)}</td>
                        <td className="num px-3 text-[13px]">{r.scan_count}</td>
                        <td className="px-3"><span className="inline-block rounded-full bg-[#FFF1E6] px-2 py-0.5 text-[11.5px] font-medium text-[#B5590F]">{t("fp.unmappedPin")}</span></td>
                        <td className="px-3"><span className="inline-block rounded-full bg-[#FFF1E6] px-2 py-0.5 text-[11.5px] font-medium text-[#B5590F]">{t("fp.unmappedPin")}</span></td>
                        <td className="px-3 pr-5 text-right"><button data-testid="fp-map-pin" onClick={() => navigate("/integrations/fingerspot")} className="rounded-full bg-[#0071E3] px-3 py-1.5 text-[12px] font-medium text-white transition-all active:scale-[0.97]">{t("fp.mapPin")}</button></td>
                      </tr>
                    );
                  }
                  return (
                    <Fragment key={r.id}>
                      <tr data-testid="fp-row" className={`align-middle hover:bg-white/50 ${open ? "" : "border-b border-black/[0.04]"}`}>
                        <td className="py-3 pl-5 pr-3 align-middle">
                          <div className="flex items-start gap-2.5">
                            <button onClick={() => openQuickProfile(r.employee_id)} className="mt-0.5 shrink-0">
                              <EmployeeAvatar id={r.employee_id} nama={r.employee_name} hasPhoto={r.has_photo} size={32} />
                            </button>
                            <div className="min-w-0">
                              <span className="flex items-center gap-1.5"><button onClick={() => openQuickProfile(r.employee_id)} className="block truncate text-left text-[13px] font-medium text-[#1D1D1F] hover:text-[#0071E3]">{r.employee_name}</button>{r.verified && !r.is_jarit && <VerifiedBadge size={13} />}<JaritBadge show={r.is_jarit} /></span>
                              <p className="num text-[11.5px] text-[#8E8E93]">{r.employee_nip || "—"} · {r.attendance_date}</p>
                              <button
                                onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                                className="mt-1 flex items-center gap-1 text-[11.5px] font-medium text-[#0071E3]"
                              >
                                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {t("fp.todayScans", { n: r.scan_count })}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="num whitespace-nowrap px-3 text-[13px] align-middle">{r.first_scan_at?.slice(0, 5)} → {r.last_scan_at?.slice(0, 5)}</td>
                        <td className="num px-3 text-[13px] align-middle">{r.scan_count}</td>
                        <td className="px-3 align-middle">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[11.5px] font-medium ${TYPE_STYLE[r.attendance_type] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
                            {r.manual_day_value != null ? `${r.manual_day_value}${t("fp.dayUnit")} ${t("fp.manualSuffix")}` : r.calculated_day_value != null ? `${r.calculated_day_value}${t("fp.dayUnit")}` : tyLabel(r.attendance_type)}
                          </span>
                          {needs && r.reason_code && <p className="mt-1 text-[11px] text-[#B5590F]">{rsLabel(r.reason_code)}</p>}
                        </td>
                        <td className="px-3 align-middle">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[11.5px] font-medium ${STATUS_STYLE[r.status] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
                            {stLabel(r.status)}
                          </span>
                          {r.reviewed_by && <p className="mt-1 flex items-center gap-1 text-[10.5px] text-[#8E8E93]">{t("ma.by", { name: r.reviewed_by })} <VerifiedBadge size={12} /></p>}
                        </td>
                        <td className="px-3 pr-5 text-right align-middle">
                          {editing[r.id] ? (
                            <div className="ml-auto flex w-fit flex-col items-stretch gap-3 py-3.5">
                              <div className="flex justify-end gap-2">
                                <RvBtn onClick={() => review(r.id, "approve", 1)} busy={busy === r.id}>1.0</RvBtn>
                                <RvBtn onClick={() => review(r.id, "approve", 0.5)} busy={busy === r.id}>0.5</RvBtn>
                                <RvBtn onClick={() => review(r.id, "approve", 0)} busy={busy === r.id}>0</RvBtn>
                                <RvBtn onClick={() => review(r.id, "ignore")} busy={busy === r.id} tone="gray">{t("fp.ignore")}</RvBtn>
                              </div>
                              <input
                                value={notes[r.id] || ""}
                                onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                                placeholder={r.status === "AUTO_APPROVED" ? t("fp.reasonReq") : t("fp.noteOpt")}
                                className="h-8 w-full rounded-md border border-[#E1E1E6] bg-white/70 px-2.5 text-[12px]"
                              />
                              <div className="flex w-full items-center justify-end gap-3">
                                {r.manual_override && (
                                  <button data-testid="fp-revert" onClick={() => revert(r.id)} disabled={busy === r.id} className="text-[11px] font-medium text-[#0071E3]">{t("fp.revertAuto")}</button>
                                )}
                                <button data-testid="fp-edit-cancel" onClick={() => setEditing((e) => ({ ...e, [r.id]: false }))} className="flex items-center gap-1 text-[11.5px] font-medium text-[#8E8E93] transition-colors hover:text-[#6E6E73]"><X className="h-3 w-3" /> {t("fp.cancel")}</button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-end gap-1">
                              <button data-testid="fp-edit-btn" onClick={() => setEditing((e) => ({ ...e, [r.id]: true }))} className={`flex items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3 py-1.5 text-[12px] font-medium text-[#0071E3] transition-all hover:bg-white active:scale-[0.97] ${readOnly ? "hidden" : ""}`}><Pencil className="h-3.5 w-3.5" /> {t("fp.edit")}</button>
                              {r.manual_override && (
                                <button data-testid="fp-revert" onClick={() => revert(r.id)} disabled={busy === r.id} className="text-[11px] font-medium text-[#0071E3]">{t("fp.revertAuto")}</button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr className="border-b border-black/[0.04]">
                          <td colSpan={6} className="px-5 pb-3 pt-0">
                            <div className="space-y-1 pl-0 sm:max-w-sm">
                              {((r.scan_events && r.scan_events.length) ? r.scan_events : (r.raw_scans || []).map((s) => ({ t: s.slice(11, 16), kind: null }))).map((s, i) => (
                                <div key={i} className="flex items-center justify-between gap-6 rounded-md bg-black/[0.04] px-2 py-1">
                                  <span className="num text-[11.5px]">{s.t}</span>
                                  <span className={`text-[11px] ${SCAN_STYLE(s.kind)}`}>{scanLabel(s.kind)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>

      <Dialog open={manOpen} onOpenChange={setManOpen}>
        <DialogContent data-testid="fp-manual-dialog" className="gap-0 rounded-[20px] border border-white/50 bg-white/80 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-2xl sm:max-w-[460px]">
          <DialogHeader className="text-left">
            <DialogTitle className="text-[18px] font-semibold tracking-tight">{t("fp.manual")}</DialogTitle>
            <p className="text-[12.5px] text-[#8E8E93]">{t("fp.manualSub")}</p>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <Select value={man.employee_id} onValueChange={(v) => setMan((m) => ({ ...m, employee_id: v }))}>
              <SelectTrigger data-testid="fp-manual-employee" className="h-11 rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]">
                <SelectValue placeholder={t("fp.selEmpPh")} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] rounded-xl">
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.nama}{e.emp_code ? ` (${e.emp_code})` : ""}{e.fingerspot_pin ? ` · PIN ${e.fingerspot_pin}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <input data-testid="fp-manual-date" type="date" value={man.date}
                onChange={(e) => setMan((m) => ({ ...m, date: e.target.value }))}
                className="h-11 flex-1 rounded-xl border border-[#E1E1E6] bg-white/70 px-3 text-[14px]" />
              <Select value={String(man.day_value)} onValueChange={(v) => setMan((m) => ({ ...m, day_value: v, action: v === "ignore" ? "ignore" : "approve" }))}>
                <SelectTrigger data-testid="fp-manual-value" className="h-11 w-[150px] rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="1">{t("appr.dayFull")}</SelectItem>
                  <SelectItem value="0.5">{t("appr.dayHalf")}</SelectItem>
                  <SelectItem value="0">{t("appr.dayZero")}</SelectItem>
                  <SelectItem value="ignore">{t("fp.ignore")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <input data-testid="fp-manual-note" value={man.note}
              onChange={(e) => setMan((m) => ({ ...m, note: e.target.value }))}
              placeholder={t("fp.noteReqPh")}
              className="h-11 w-full rounded-xl border border-[#E1E1E6] bg-white/70 px-3 text-[14px]" />
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setManOpen(false)} className="rounded-full px-4 py-2 text-[13px] font-medium text-[#6E6E73] hover:bg-black/[0.04]">{t("fp.cancel")}</button>
              <button data-testid="fp-manual-submit" onClick={submitManual} disabled={busy === "manual"}
                className={`flex items-center gap-1.5 rounded-full bg-[#0071E3] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#0077ED] disabled:opacity-50 ${readOnly ? "hidden" : ""}`}>
                {busy === "manual" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("fp.save")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent data-testid="fp-export-dialog" className="gap-0 rounded-[20px] border border-white/50 bg-white/80 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.18)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl sm:max-w-[460px]">
          <DialogHeader className="text-left">
            <DialogTitle className="text-[18px] font-semibold tracking-tight">{t("fp.exportTitle")}</DialogTitle>
            <p className="text-[12.5px] text-[#8E8E93]">{t("fp.exportSub")}</p>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="flex gap-2">
              <Select value={String(exp.bulan)} onValueChange={(v) => setExp((e) => ({ ...e, bulan: Number(v) }))}>
                <SelectTrigger data-testid="fp-export-month" className="h-11 flex-1 rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] rounded-xl">
                  {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((mn, i) => (
                    <SelectItem key={mn} value={String(i + 1)}>{mn}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                data-testid="fp-export-year"
                type="number"
                value={exp.tahun}
                onChange={(e) => setExp((p) => ({ ...p, tahun: Number(e.target.value) }))}
                className="h-11 w-[110px] rounded-xl border border-[#E1E1E6] bg-white/70 px-3 text-[14px]"
              />
            </div>
            <Select value={exp.scope} onValueChange={(v) => setExp((e) => ({ ...e, scope: v }))}>
              <SelectTrigger data-testid="fp-export-scope" className="h-11 rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">{t("fp.allEmp")}</SelectItem>
                <SelectItem value="one">{t("fp.oneEmp")}</SelectItem>
              </SelectContent>
            </Select>
            {exp.scope === "one" && (
              <Select value={exp.employee_id} onValueChange={(v) => setExp((e) => ({ ...e, employee_id: v }))}>
                <SelectTrigger data-testid="fp-export-employee" className="h-11 rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]">
                  <SelectValue placeholder={t("fp.selEmpPh")} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] rounded-xl">
                  {employees.filter((e) => (e.fingerspot_pin || "").trim()).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.nama}{e.emp_code ? ` (${e.emp_code})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setExportOpen(false)} className="rounded-full px-4 py-2 text-[13px] font-medium text-[#6E6E73] hover:bg-black/[0.04]">{t("fp.cancel")}</button>
              <button
                data-testid="fp-export-confirm"
                onClick={doExport}
                disabled={exporting}
                className="flex items-center gap-1.5 rounded-full bg-[#0071E3] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#0077ED] disabled:opacity-50"
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {t("fp.exportPdf")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const RvBtn = ({ children, onClick, busy, tone }) => (
  <button
    data-testid="fp-review-btn"
    onClick={onClick}
    disabled={busy}
    className={`rounded-md px-2 py-1 text-[11.5px] font-semibold transition-all active:scale-95 disabled:opacity-50 ${
      tone === "gray" ? "bg-[#F1F1F4] text-[#6E6E73]" : "bg-[#0071E3] text-white hover:bg-[#0077ED]"
    }`}
  >
    {children}
  </button>
);
